import React from 'react';
import * as yup from 'yup';
import { Box, Grid, Typography } from '@mui/material';
import { FormikHelpers } from 'formik';

import {
  SparkForm,
  SparkFormImageUploader,
  SparkTextField,
  SparkFormResetButton,
  SparkFormButton,
  SparkFormPhoneInput,
} from 'sharedComponents/SparkForm';
import type {
  ApplicationUser,
  ApplicationUserAttribute,
  SingleApplicationUserResponse,
} from 'types/ApplicationUser';
import { UseMutateAsyncFunction } from 'react-query';
import { useCountries } from 'hooks/Countries/useCountries';
import { useResize } from 'hooks/common';
import { PHONE_NUMBER_PATTERN } from 'constants/regex';

type BasicInfoFormType = {
  applicationUser: ApplicationUser | undefined;
  UpdateApplicationUser: UseMutateAsyncFunction<
    SingleApplicationUserResponse,
    unknown,
    Partial<ApplicationUserAttribute>,
    unknown
  >;
};

type BasicInfoFormValues = {
  name: string;
  email: string;
  surname: string;
  mobileNumber: string;
  profilePictureURL: string;
};

export default function BasicInfoForm({
  applicationUser,
  UpdateApplicationUser,
}: BasicInfoFormType) {
  const { isMobile } = useResize();
  const { countryCodeDropdownOptions: countriesOptions } = useCountries();
  const validationSchema = yup.object({
    name: yup.string().required('Name is Required'),
    email: yup.string().email('Invalid email').required('Email is Required'),
    surname: yup.string().required('Surname is Required'),
    mobileNumber: yup
      .string()
      .matches(PHONE_NUMBER_PATTERN, 'Phone number is not valid'),
    profilePictureURL: yup.string(),
  });

  const initialValues = {
    name: applicationUser?.attributes.name ?? '',
    email: applicationUser?.attributes.email ?? '',
    surname: applicationUser?.attributes.surname ?? '',
    mobileNumber: applicationUser?.attributes.mobileNumber ?? '',
    profilePictureURL: applicationUser?.attributes.profilePictureURL ?? '',
  };

  const handleSave = async (
    values: BasicInfoFormValues,
    formikHelpers: FormikHelpers<BasicInfoFormValues>
  ) => {
    const updatedUser = await UpdateApplicationUser(values);
    const { name, email, surname, mobileNumber, profilePictureURL } =
      updatedUser.data.attributes;
    formikHelpers.setValues((prev) => ({
      ...prev,
      name,
      email,
      surname,
      mobileNumber,
      profilePictureURL: profilePictureURL || '',
    }));
  };

  const onImageUpload = async (profilePictureURL: string) => {
    await UpdateApplicationUser({ profilePictureURL });
  };

  return (
    <Grid container>
      <Typography
        variant='h5'
        sx={{
          fontFamily: 'Mont-semibold',
        }}
      >
        Basic Info
      </Typography>

      <Box
        width='100%'
        bgcolor='white'
        borderRadius='20px'
        border={(theme) => `1px solid ${theme.palette.borderColor}`}
      >
        <SparkForm<BasicInfoFormValues>
          initialValues={initialValues}
          onSave={handleSave}
          validationSchema={validationSchema}
          containerProps={{ spacing: 2, padding: 2 }}
        >
          <SparkFormImageUploader
            name='profilePictureURL'
            containerName='public-assets'
            onUpload={onImageUpload}
          />
          <SparkTextField
            name='name'
            label='Name'
            placeholder='Enter your name'
            size={isMobile ? 12 : 6}
          />
          <SparkTextField
            name='email'
            label='Email'
            placeholder='Enter your email'
            size={isMobile ? 12 : 6}
            autoLowerCase={true}
          />
          <SparkTextField
            name='surname'
            label='Surname'
            placeholder='Enter your surname'
            size={isMobile ? 12 : 6}
          />
          <SparkFormPhoneInput
            name='mobileNumber'
            label='Phone'
            countriesOptions={countriesOptions}
            size={isMobile ? 12 : 6}
          />

          <Grid
            item
            container
            xs={12}
            gap={2}
            justifyContent='flex-end'
            flexWrap='nowrap'
          >
            <SparkFormResetButton
              variant='outlined'
              sx={{ width: '200px', padding: '10px 20px', marginTop: '10px' }}
            >
              Cancel
            </SparkFormResetButton>
            <SparkFormButton
              sx={{ width: '200px', padding: '10px 20px', marginTop: '10px' }}
            >
              Save
            </SparkFormButton>
          </Grid>
        </SparkForm>
      </Box>
    </Grid>
  );
}
